import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  CreditCard, 
  Wallet, 
  Building2, 
  Plus, 
  Trash2, 
  X, 
  Loader2,
  Check,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import { API } from "../utils/api";

const PaymentMethodsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  
  // Form states for different payment types
  const [cardForm, setCardForm] = useState({
    card_number: "",
    card_holder: "",
    expiry_month: "",
    expiry_year: "",
    card_type: "credit" // credit or debit
  });
  
  const [upiForm, setUpiForm] = useState({
    upi_id: ""
  });
  
  const [bankForm, setBankForm] = useState({
    account_number: "",
    ifsc_code: "",
    account_holder: "",
    bank_name: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("fleety_user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchPaymentMethods();
    }
  }, [user]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods/${user.id}`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 16);
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(" ") : cleaned;
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    if (name === "card_number") {
      setCardForm(prev => ({ ...prev, [name]: formatCardNumber(value) }));
    } else if (name === "expiry_month") {
      const cleaned = value.replace(/\D/g, "").slice(0, 2);
      if (cleaned === "" || (parseInt(cleaned) >= 0 && parseInt(cleaned) <= 12)) {
        setCardForm(prev => ({ ...prev, [name]: cleaned }));
      }
    } else if (name === "expiry_year") {
      const cleaned = value.replace(/\D/g, "").slice(0, 2);
      setCardForm(prev => ({ ...prev, [name]: cleaned }));
    } else {
      setCardForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpiChange = (e) => {
    setUpiForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    if (name === "account_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 18);
      setBankForm(prev => ({ ...prev, [name]: cleaned }));
    } else if (name === "ifsc_code") {
      setBankForm(prev => ({ ...prev, [name]: value.toUpperCase().slice(0, 11) }));
    } else {
      setBankForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateCard = () => {
    const cardNum = cardForm.card_number.replace(/\s/g, "");
    if (cardNum.length < 15) {
      setError("Please enter a valid card number");
      return false;
    }
    if (!cardForm.card_holder.trim()) {
      setError("Please enter card holder name");
      return false;
    }
    if (!cardForm.expiry_month || !cardForm.expiry_year) {
      setError("Please enter expiry date");
      return false;
    }
    return true;
  };

  const validateUpi = () => {
    if (!upiForm.upi_id.includes("@")) {
      setError("Please enter a valid UPI ID (e.g., name@upi)");
      return false;
    }
    return true;
  };

  const validateBank = () => {
    if (bankForm.account_number.length < 9) {
      setError("Please enter a valid account number");
      return false;
    }
    if (bankForm.ifsc_code.length !== 11) {
      setError("Please enter a valid 11-character IFSC code");
      return false;
    }
    if (!bankForm.account_holder.trim()) {
      setError("Please enter account holder name");
      return false;
    }
    if (!bankForm.bank_name.trim()) {
      setError("Please enter bank name");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setError("");
    let isValid = false;
    let paymentData = { user_id: user.id, type: selectedType };

    if (selectedType === "card") {
      isValid = validateCard();
      if (isValid) {
        paymentData = {
          ...paymentData,
          card_number: cardForm.card_number.replace(/\s/g, ""),
          card_holder: cardForm.card_holder,
          expiry_month: cardForm.expiry_month,
          expiry_year: cardForm.expiry_year,
          card_type: cardForm.card_type,
          last_four: cardForm.card_number.replace(/\s/g, "").slice(-4)
        };
      }
    } else if (selectedType === "upi") {
      isValid = validateUpi();
      if (isValid) {
        paymentData = {
          ...paymentData,
          upi_id: upiForm.upi_id
        };
      }
    } else if (selectedType === "bank") {
      isValid = validateBank();
      if (isValid) {
        paymentData = {
          ...paymentData,
          account_number: bankForm.account_number,
          ifsc_code: bankForm.ifsc_code,
          account_holder: bankForm.account_holder,
          bank_name: bankForm.bank_name,
          last_four: bankForm.account_number.slice(-4)
        };
      }
    }

    if (!isValid) return;

    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/payment-methods`, paymentData);
      if (response.data.success) {
        fetchPaymentMethods();
        closeModal();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save payment method");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (methodId) => {
    if (!window.confirm("Are you sure you want to remove this payment method?")) return;

    try {
      await axios.delete(`${API}/payment-methods/${methodId}`);
      setPaymentMethods(prev => prev.filter(m => m.id !== methodId));
    } catch (error) {
      console.error("Error deleting payment method:", error);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      await axios.put(`${API}/payment-methods/${methodId}/default`, {
        user_id: user.id
      });
      setPaymentMethods(prev => prev.map(m => ({
        ...m,
        is_default: m.id === methodId
      })));
    } catch (error) {
      console.error("Error setting default:", error);
    }
  };

  const closeModal = () => {
    setShowAddForm(false);
    setSelectedType(null);
    setError("");
    setCardForm({ card_number: "", card_holder: "", expiry_month: "", expiry_year: "", card_type: "credit" });
    setUpiForm({ upi_id: "" });
    setBankForm({ account_number: "", ifsc_code: "", account_holder: "", bank_name: "" });
  };

  const getPaymentIcon = (type) => {
    switch (type) {
      case "card": return CreditCard;
      case "upi": return Wallet;
      case "bank": return Building2;
      default: return CreditCard;
    }
  };

  const getPaymentLabel = (method) => {
    switch (method.type) {
      case "card":
        return `${method.card_type === "credit" ? "Credit" : "Debit"} Card •••• ${method.last_four}`;
      case "upi":
        return `UPI: ${method.upi_id}`;
      case "bank":
        return `${method.bank_name} •••• ${method.last_four}`;
      default:
        return "Payment Method";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3FA9F5] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-methods-page">
      {/* Top Spacer for mobile */}
      <div className="h-6 bg-white"></div>
      
      {/* Header */}
      <header className="bg-white px-4 py-4 shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate("/home")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Payment Methods</h1>
          <Button
            onClick={() => setShowAddForm(true)}
            className="ml-auto bg-[#3FA9F5] hover:bg-[#2196F3] text-white"
            data-testid="add-payment-btn"
          >
            <Plus size={18} className="mr-1" />
            Add New
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Cash on Delivery - Always available */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border-2 border-green-500" data-testid="cod-payment">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-xl">💵</span>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Cash on Delivery</h3>
              <p className="text-sm text-gray-500">Pay when your order arrives</p>
            </div>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Always Available</span>
          </div>
        </div>

        {/* Saved Payment Methods */}
        {paymentMethods.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Saved Methods</h2>
            {paymentMethods.map((method) => {
              const IconComponent = getPaymentIcon(method.type);
              return (
                <div 
                  key={method.id} 
                  className={`bg-white rounded-xl shadow-sm p-4 ${method.is_default ? 'border-2 border-[#3FA9F5]' : 'border border-gray-100'}`}
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 ${method.is_default ? 'bg-[#3FA9F5]/10' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                      <IconComponent size={24} className={method.is_default ? 'text-[#3FA9F5]' : 'text-gray-500'} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{getPaymentLabel(method)}</h3>
                        {method.is_default && (
                          <span className="text-xs bg-[#3FA9F5] text-white px-2 py-0.5 rounded-full">Default</span>
                        )}
                      </div>
                      {method.type === "card" && (
                        <p className="text-sm text-gray-500">{method.card_holder} • Expires {method.expiry_month}/{method.expiry_year}</p>
                      )}
                      {!method.is_default && (
                        <button 
                          onClick={() => handleSetDefault(method.id)}
                          className="mt-1 text-sm text-[#3FA9F5] hover:underline flex items-center gap-1"
                        >
                          <Check size={14} />
                          Set as default
                        </button>
                      )}
                    </div>
                    <button 
                      onClick={() => handleDelete(method.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                      data-testid={`delete-payment-${method.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {paymentMethods.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CreditCard size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No saved payment methods</p>
            <p className="text-sm">Add a card or UPI for faster checkout</p>
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-50 rounded-xl flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-500 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-medium">Secure Payments</p>
            <p className="text-sm text-blue-600">Your payment information is encrypted and secure. We never store your full card number.</p>
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedType ? `Add ${selectedType === "card" ? "Card" : selectedType === "upi" ? "UPI" : "Bank Account"}` : "Add Payment Method"}
                </h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              {!selectedType ? (
                /* Payment Type Selection */
                <div className="space-y-3">
                  <button
                    onClick={() => setSelectedType("card")}
                    className="w-full p-4 border-2 border-gray-100 rounded-xl hover:border-[#3FA9F5] flex items-center gap-4 transition-all"
                    data-testid="select-card"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <CreditCard size={24} className="text-gray-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Credit / Debit Card</p>
                      <p className="text-sm text-gray-500">Visa, Mastercard, RuPay</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedType("upi")}
                    className="w-full p-4 border-2 border-gray-100 rounded-xl hover:border-[#3FA9F5] flex items-center gap-4 transition-all"
                    data-testid="select-upi"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <Wallet size={24} className="text-gray-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">UPI</p>
                      <p className="text-sm text-gray-500">Google Pay, PhonePe, Paytm</p>
                    </div>
                  </button>

                  <button
                    onClick={() => setSelectedType("bank")}
                    className="w-full p-4 border-2 border-gray-100 rounded-xl hover:border-[#3FA9F5] flex items-center gap-4 transition-all"
                    data-testid="select-bank"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <Building2 size={24} className="text-gray-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Net Banking</p>
                      <p className="text-sm text-gray-500">All major banks supported</p>
                    </div>
                  </button>
                </div>
              ) : selectedType === "card" ? (
                /* Card Form */
                <div className="space-y-4">
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => setCardForm(prev => ({ ...prev, card_type: "credit" }))}
                      className={`flex-1 py-2 rounded-lg border-2 ${cardForm.card_type === "credit" ? "border-[#3FA9F5] bg-[#3FA9F5]/5" : "border-gray-200"}`}
                    >
                      Credit Card
                    </button>
                    <button
                      onClick={() => setCardForm(prev => ({ ...prev, card_type: "debit" }))}
                      className={`flex-1 py-2 rounded-lg border-2 ${cardForm.card_type === "debit" ? "border-[#3FA9F5] bg-[#3FA9F5]/5" : "border-gray-200"}`}
                    >
                      Debit Card
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                    <Input
                      type="text"
                      name="card_number"
                      placeholder="1234 5678 9012 3456"
                      value={cardForm.card_number}
                      onChange={handleCardChange}
                      className="h-12 rounded-xl"
                      data-testid="card-number-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Card Holder Name</label>
                    <Input
                      type="text"
                      name="card_holder"
                      placeholder="Name on card"
                      value={cardForm.card_holder}
                      onChange={handleCardChange}
                      className="h-12 rounded-xl"
                      data-testid="card-holder-input"
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Month</label>
                      <Input
                        type="text"
                        name="expiry_month"
                        placeholder="MM"
                        value={cardForm.expiry_month}
                        onChange={handleCardChange}
                        className="h-12 rounded-xl"
                        data-testid="expiry-month-input"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Year</label>
                      <Input
                        type="text"
                        name="expiry_year"
                        placeholder="YY"
                        value={cardForm.expiry_year}
                        onChange={handleCardChange}
                        className="h-12 rounded-xl"
                        data-testid="expiry-year-input"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-gray-400">CVV will be asked during checkout for security</p>
                </div>
              ) : selectedType === "upi" ? (
                /* UPI Form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
                    <Input
                      type="text"
                      name="upi_id"
                      placeholder="yourname@upi"
                      value={upiForm.upi_id}
                      onChange={handleUpiChange}
                      className="h-12 rounded-xl"
                      data-testid="upi-id-input"
                    />
                  </div>
                  <p className="text-xs text-gray-400">Enter your UPI ID linked to any UPI app</p>
                </div>
              ) : (
                /* Bank Account Form */
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <Input
                      type="text"
                      name="account_number"
                      placeholder="Enter account number"
                      value={bankForm.account_number}
                      onChange={handleBankChange}
                      className="h-12 rounded-xl"
                      data-testid="account-number-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                    <Input
                      type="text"
                      name="ifsc_code"
                      placeholder="e.g., SBIN0001234"
                      value={bankForm.ifsc_code}
                      onChange={handleBankChange}
                      className="h-12 rounded-xl"
                      data-testid="ifsc-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                    <Input
                      type="text"
                      name="account_holder"
                      placeholder="Name as per bank records"
                      value={bankForm.account_holder}
                      onChange={handleBankChange}
                      className="h-12 rounded-xl"
                      data-testid="account-holder-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <Input
                      type="text"
                      name="bank_name"
                      placeholder="e.g., State Bank of India"
                      value={bankForm.bank_name}
                      onChange={handleBankChange}
                      className="h-12 rounded-xl"
                      data-testid="bank-name-input"
                    />
                  </div>
                </div>
              )}

              {error && (
                <p className="text-red-500 text-sm mt-4">{error}</p>
              )}

              {selectedType && (
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => setSelectedType(null)}
                    variant="outline"
                    className="flex-1 h-12 rounded-xl"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 h-12 bg-[#3FA9F5] hover:bg-[#2196F3] text-white rounded-xl"
                    data-testid="save-payment-btn"
                  >
                    {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsPage;
